import { useMemo } from 'react';
import {
  Binary,
  Bot,
  PlugZap,
  Ruler,
  ScanLine,
  ShieldCheck,
  Sigma,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';

import {
  PageContainer,
  PageHeader,
  Panel,
  StatusBadge,
} from '@/components/fleetguardian';
import { cn } from '@/lib/utils';
import {
  INTELLIGENCE_LAYERS,
  LIVE_LAYERS,
  countMethods,
  type IntelligenceLayer,
  type IntelligenceMethod,
} from '@/lib/intelligenceMethods';

/**
 * Intelligence Methods — the credibility page for the AI Detective.
 *
 * Every entry on this page is a technique that is actually implemented in this
 * repository, and every entry cites the module it lives in. The page is
 * deliberately static: it is a claim about the codebase, not about the data, so
 * there is no fetch, no loading state and nothing that can drift at runtime.
 * The single source of truth is `@/lib/intelligenceMethods`.
 */

const LAYER_ICONS: Record<string, LucideIcon> = {
  deterministic: Ruler,
  baselines: Sigma,
  statistical: Binary,
  llm: Bot,
  forecasting: TrendingUp,
  'data-trust': ScanLine,
  'fact-layer': ShieldCheck,
  dormant: PlugZap,
};

export default function IntelligenceMethods(): JSX.Element {
  const liveMethods = useMemo(() => countMethods(LIVE_LAYERS), []);

  return (
    <PageContainer>
      <PageHeader
        title="Intelligence Methods"
        subtitle="The mathematics behind the Detective — every method, named. Nothing here is a black box."
      />

      <Panel className="px-4 py-4 sm:px-5">
        <p className="max-w-4xl text-sm leading-relaxed text-gray-600 dark:text-muted-foreground">
          Fuel Bench does not produce a single opaque &ldquo;fraud score&rdquo;. It runs{' '}
          <strong className="font-semibold text-gray-900 dark:text-foreground">
            {liveMethods} distinct techniques
          </strong>{' '}
          across {LIVE_LAYERS.length} layers, each answering a different question,
          each auditable on its own. Every method below is implemented in this
          platform and names the module it runs in, so any finding can be traced from
          the alert back to the line of code that raised it. The final section lists
          what is built but not yet receiving data — because a capability gap an
          auditor discovers for themselves is worth far less than one we declare.
        </p>
      </Panel>

      {INTELLIGENCE_LAYERS.map((layer) => (
        <LayerSection key={layer.id} layer={layer} />
      ))}

      <Panel className="px-4 py-4 sm:px-5">
        <p className="text-xs leading-relaxed text-gray-500 dark:text-muted-foreground">
          Source paths are relative to the repository root. Layers run in sequence on
          every transaction: deterministic rules fire first and carry the evidential
          weight; learned baselines and statistical scoring add context and ranking;
          the language layer only ever explains and cites what the layers beneath it
          already established, and refuses when it cannot. Nothing in this platform
          declares fraud — every method produces an indicator for an investigator to
          weigh, and the decision stays with a person.
        </p>
      </Panel>
    </PageContainer>
  );
}

function LayerSection({ layer }: { layer: IntelligenceLayer }): JSX.Element {
  const Icon = LAYER_ICONS[layer.id] ?? ShieldCheck;
  const dormant = layer.status === 'dormant';

  return (
    <section
      aria-labelledby={`layer-${layer.id}`}
      className={cn(
        'space-y-3',
        // The dormant block is fenced off above a rule so it can never be
        // skim-read as part of the live capability list.
        dormant && 'mt-2 border-t border-amber-300/70 pt-6 dark:border-amber-500/30',
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
            dormant
              ? 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10'
              : 'border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/5',
          )}
        >
          <Icon
            className={cn('h-4 w-4', dormant ? 'text-amber-600' : 'text-primary')}
          />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-[11px] font-semibold tracking-widest text-gray-400 dark:text-muted-foreground">
              {layer.number}
            </span>
            <h2
              id={`layer-${layer.id}`}
              className="text-base font-bold leading-tight text-gray-900 dark:text-foreground"
            >
              {layer.title}
            </h2>
            <span className="text-[11px] text-gray-400 dark:text-muted-foreground">
              {layer.methods.length}{' '}
              {layer.methods.length === 1 ? 'method' : 'methods'}
            </span>
            {dormant && <StatusBadge tone="warning">Awaiting data</StatusBadge>}
          </div>
          <p className="mt-0.5 max-w-3xl text-[13px] leading-relaxed text-gray-500 dark:text-muted-foreground">
            {layer.tagline}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
        {layer.methods.map((method) => (
          <MethodCard key={method.name} method={method} inDormantLayer={dormant} />
        ))}
      </div>
    </section>
  );
}

function MethodCard({
  method,
  inDormantLayer,
}: {
  method: IntelligenceMethod;
  inDormantLayer: boolean;
}): JSX.Element {
  return (
    <Panel hoverable className="flex h-full flex-col gap-2 p-4">
      <h3 className="text-[13px] font-bold leading-snug text-gray-900 dark:text-foreground">
        {method.name}
      </h3>

      <p className="flex-1 text-[13px] leading-relaxed text-gray-600 dark:text-muted-foreground">
        {method.purpose}
      </p>

      <p
        className={cn(
          'break-all font-mono text-[10.5px] leading-relaxed',
          'text-gray-400 dark:text-muted-foreground/70',
        )}
      >
        source: {method.source}
      </p>

      <div className="flex flex-wrap gap-1.5">
        {method.surfaces.map((surface) => (
          <StatusBadge key={surface} tone="neutral" showDot={false}>
            {surface}
          </StatusBadge>
        ))}
        {/* Inside the dormant block the section heading already says so — a
            per-card badge there would be noise, not information. */}
        {method.caveat && !inDormantLayer && (
          <StatusBadge tone="warning">Not live</StatusBadge>
        )}
      </div>

      {method.caveat && (
        <p className="text-[11px] leading-relaxed text-amber-700 dark:text-amber-500">
          {method.caveat}
        </p>
      )}
    </Panel>
  );
}
